import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Truck, Search, Plus, Loader2, Eye, Package, Clock, CheckCircle, X, ArrowLeft, Printer, Download, Upload, FileText, AlertTriangle, Link2, LayoutGrid, List, DollarSign, Pencil, Save, Check, Ban } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { dispatchAPI, branchAPI, fileAPI, fetchPaged } from '../../services/api';
import { toast } from 'sonner';
import Pagination from '../ui/Pagination';
import DispatchKanban from './DispatchKanban';
import SortableHeader from '../ui/SortableHeader';
import FreightTracker from './FreightTracker';
import ExtraChargesTracker from './ExtraChargesTracker';
import jsPDF from 'jspdf';

const statusConfig = {
  'Awaiting Invoice': { bg: 'bg-gray-50', text: 'text-gray-600', border: 'border-gray-200', color: '#6b7280' },
  'Pending': { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', color: '#f59e0b' },
  'In Transit': { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', color: '#3b82f6' },
  'Delivered': { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', color: '#059669' },
  'Cancelled': { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', color: '#dc2626' },
};

const DispatchModule = () => {
  const [view, setView] = useState('list');
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'kanban'
  const [dispatches, setDispatches] = useState([]);
  const [kanbanDispatches, setKanbanDispatches] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, in_transit: 0, delivered: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [createFromOCId, setCreateFromOCId] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', order: 'desc' });
  const handlePageSizeChange = (v) => { setPageSize(v); setPage(1); };
  const handleSort = (key, order) => { setSortConfig({ key, order }); setPage(1); };
  const location = useLocation();

  // Handle navigation from OC detail with createFromOC state
  useEffect(() => {
    if (location.state?.createFromOC) {
      setCreateFromOCId(location.state.createFromOC);
      setShowCreate(true);
      // Clear the state to prevent re-triggering
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Handle deep-link from ?view= query param (e.g., from Item History)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const viewId = params.get('view');
    if (viewId) {
      setSelectedId(viewId);
      setView('detail');
      // Clear the query param to prevent re-triggering
      window.history.replaceState({}, document.title, location.pathname);
    }
  }, [location.search, location.pathname]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, page_size: pageSize };
      if (search) params.search = search;
      if (statusFilter) params.status_filter = statusFilter;
      if (sortConfig.key) { params.sort_by = sortConfig.key; params.sort_order = sortConfig.order; }
      const [result, st] = await Promise.all([fetchPaged('/dispatches', params), dispatchAPI.getStats()]);
      setDispatches(result.items);
      setTotal(result.total);
      setTotalPages(result.total_pages);
      setStats(st);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [search, statusFilter, page, pageSize, sortConfig]);

  // Fetch all dispatches for Kanban view (unfiltered, larger page size)
  const fetchKanbanData = useCallback(async () => {
    try {
      const result = await fetchPaged('/dispatches', { page: 1, page_size: 200 });
      setKanbanDispatches(result.items);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { if (viewMode === 'kanban') fetchKanbanData(); }, [viewMode, fetchKanbanData]);

  const handleKanbanRefresh = useCallback(() => {
    fetchKanbanData();
    // Also refresh stats
    dispatchAPI.getStats().then(setStats).catch(() => {});
  }, [fetchKanbanData]);

  if (viewMode === 'freight') {
    return <FreightTracker onBack={() => setViewMode('list')} />;
  }

  if (viewMode === 'extra-charges') {
    return <ExtraChargesTracker onBack={() => setViewMode('list')} />;
  }

  if (view === 'detail' && selectedId) {
    return <DispatchDetail dispatchId={selectedId} onBack={() => { setView('list'); setSelectedId(null); fetchData(); }} />;
  }

  if (showCreate) {
    return <DispatchCreateForm prefillOCId={createFromOCId} onBack={() => { setShowCreate(false); setCreateFromOCId(null); fetchData(); }} />;
  }

  return (
    <div className="p-5 lg:p-6" data-testid="dispatch-module">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>Dispatches</h1>
          <p className="text-xs text-gray-500 mt-0.5">Manage shipments, tracking & delivery</p>
        </div>
        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div className="flex bg-gray-100 p-0.5 rounded-lg" data-testid="view-mode-toggle">
            <button onClick={() => setViewMode('list')} data-testid="view-list-btn"
              className={`flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-semibold rounded-md transition-all ${viewMode === 'list' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
              <List className="w-3 h-3" /> List
            </button>
            <button onClick={() => setViewMode('kanban')} data-testid="view-kanban-btn"
              className={`flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-semibold rounded-md transition-all ${viewMode === 'kanban' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
              <LayoutGrid className="w-3 h-3" /> Kanban
            </button>
            <button onClick={() => setViewMode('freight')} data-testid="view-freight-btn"
              className={`flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-semibold rounded-md transition-all ${viewMode === 'freight' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
              <DollarSign className="w-3 h-3" /> Freight
            </button>
            <button onClick={() => setViewMode('extra-charges')} data-testid="view-extra-charges-btn"
              className={`flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-semibold rounded-md transition-all ${viewMode === 'extra-charges' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
              <DollarSign className="w-3 h-3" /> Extra Charges
            </button>
          </div>
          {/* New Dispatch button removed — dispatches are now created via OC → Ready to Dispatch workflow */}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        {[
          { label: 'Total', value: stats.total, color: '#24549c', icon: Truck },
          { label: 'Awaiting Invoice', value: stats.awaiting_invoice || 0, color: '#6b7280', icon: FileText },
          { label: 'Pending', value: stats.pending, color: '#f59e0b', icon: Clock },
          { label: 'In Transit', value: stats.in_transit, color: '#3b82f6', icon: Package },
          { label: 'Delivered', value: stats.delivered, color: '#059669', icon: CheckCircle },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-xl border border-gray-100 p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: s.color + '15' }}>
                <s.icon className="w-3.5 h-3.5" style={{ color: s.color }} />
              </div>
              <span className="text-[11px] font-medium text-gray-500">{s.label}</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{s.value}</p>
          </div>
        ))}
      </div>

      {/* Kanban View */}
      {viewMode === 'kanban' ? (
        loading && kanbanDispatches.length === 0 ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>
        ) : (
          <DispatchKanban
            dispatches={kanbanDispatches}
            onRefresh={handleKanbanRefresh}
            onViewDetail={(id) => { setSelectedId(id); setView('detail'); }}
          />
        )
      ) : (
        <>
          {/* Filters — list view only */}
          <div className="flex flex-wrap gap-3 mb-5">
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Search dispatch #, customer, OC..." data-testid="dispatch-search"
                className="w-full pl-9 pr-4 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
            </div>
            <div className="flex gap-1 bg-gray-100 p-1 rounded-xl">
              {['', 'Awaiting Invoice', 'Pending', 'In Transit', 'Delivered'].map(s => (
                <button key={s} onClick={() => { setStatusFilter(s); setPage(1); }}
                  className={`px-3 py-1.5 text-[11px] font-semibold rounded-lg transition-all ${statusFilter === s ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}>
                  {s || 'All'}
                </button>
              ))}
            </div>
          </div>

          {/* List */}
          {loading ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>
          ) : dispatches.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-xl border border-gray-100">
              <Truck className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500">No dispatches found</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/50">
                    <SortableHeader label="Dispatch #" sortKey="dispatch_number" currentSort={sortConfig} onSort={handleSort} />
                    <SortableHeader label="Customer" />
                    <SortableHeader label="OC/STN Ref" />
                    <SortableHeader label="Items" align="center" />
                    <SortableHeader label="Vehicle / Transporter" />
                    <SortableHeader label="Status" align="center" />
                    <SortableHeader label="Date" sortKey="created_at" currentSort={sortConfig} onSort={handleSort} />
                    <SortableHeader label="" align="center" />
                  </tr>
                </thead>
                <tbody>
                  {dispatches.map(d => {
                    const sc = statusConfig[d.status] || statusConfig['Pending'];
                    return (
                      <tr key={d.id} className="border-b border-gray-50 hover:bg-gray-50/50 cursor-pointer" onClick={() => { setSelectedId(d.id); setView('detail'); }}
                        data-testid={`dispatch-row-${d.id}`}>
                        <td className="px-4 py-3 font-mono font-semibold text-gray-900">{d.dispatch_number}</td>
                        <td className="px-4 py-3 text-gray-700">{d.customer_name || '-'}</td>
                        <td className="px-4 py-3 text-gray-500 text-[10px]">
                          {d.dispatch_type === 'STN' ? (
                            <div className="flex items-center gap-1"><span className="px-1.5 py-0.5 rounded bg-purple-50 text-purple-700 font-semibold text-[9px]">STN</span> {d.stn_number}</div>
                          ) : (
                            <>
                              {d.oc_number && <div>OC: {d.oc_number}</div>}
                              {d.production_number && <div>PRD: {d.production_number}</div>}
                            </>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">{d.items?.length || 0}</td>
                        <td className="px-4 py-3">
                          <div className="text-gray-700">{d.vehicle_no || '-'}</div>
                          {d.transporter_name && <div className="text-[10px] text-gray-400">{d.transporter_name}</div>}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`inline-flex px-2 py-0.5 text-[10px] font-semibold rounded-full border ${sc.bg} ${sc.text} ${sc.border}`}>{d.status}</span>
                        </td>
                        <td className="px-4 py-3 text-[10px] text-gray-500">{d.dispatch_date || '-'}</td>
                        <td className="px-4 py-3"><Eye className="w-3.5 h-3.5 text-gray-400 mx-auto" /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {dispatches.length > 0 && <Pagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={handlePageSizeChange} />}
        </>
      )}
    </div>
  );
};

// ==================== DISPATCH CREATE FORM ====================

const DispatchCreateForm = ({ onBack, prefillOCId }) => {
  const [branches, setBranches] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);  // Sync double-click guard
  const [error, setError] = useState('');
  const [attachments, setAttachments] = useState([]);
  const fileRef = useRef();

  // OC Lookup state
  const [ocSearch, setOcSearch] = useState('');
  const [ocResults, setOcResults] = useState([]);
  const [ocSearching, setOcSearching] = useState(false);
  const [showOCDropdown, setShowOCDropdown] = useState(false);
  const [linkedOC, setLinkedOC] = useState(null);
  const [ocData, setOcData] = useState(null);
  const [ocLoading, setOcLoading] = useState(false);
  const [fullyDispatched, setFullyDispatched] = useState(false);
  const ocDropdownRef = useRef();

  const [form, setForm] = useState({
    oc_reference: '', oc_number: '', production_order_id: '', production_number: '',
    customer: '', customer_name: '', items: [{ product: '', name: '', qty: 0, unit: 'PCS' }],
    from_branch: '', to_location: '', delivery_address: '', vehicle_no: '', driver_name: '', driver_phone: '',
    transporter_name: '', lr_number: '', eway_bill: '',
    dispatch_date: new Date().toISOString().split('T')[0], expected_delivery: '', notes: '',
    total_freight: '', advance_freight: '', rest_freight: 0,
  });

  useEffect(() => {
    branchAPI.getAll().then(setBranches).catch(() => {});
  }, []);

  // Auto-load OC if prefillOCId is provided (from OC detail navigation)
  useEffect(() => {
    if (prefillOCId) {
      loadOCData(prefillOCId);
    }
  }, [prefillOCId]);

  // OC search debounce
  useEffect(() => {
    if (!ocSearch.trim()) { setOcResults([]); return; }
    const timer = setTimeout(async () => {
      setOcSearching(true);
      try {
        const results = await dispatchAPI.searchOC(ocSearch);
        setOcResults(results);
        setShowOCDropdown(true);
      } catch { setOcResults([]); }
      setOcSearching(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [ocSearch]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (ocDropdownRef.current && !ocDropdownRef.current.contains(e.target)) setShowOCDropdown(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const loadOCData = async (ocId) => {
    setOcLoading(true);
    setError('');
    try {
      const data = await dispatchAPI.getFromOC(ocId);
      setOcData(data);
      setLinkedOC(data.oc);

      if (data.fully_dispatched) {
        setFullyDispatched(true);
        setError('All items in this OC have been fully dispatched. No remaining quantity.');
        setOcLoading(false);
        return;
      }
      setFullyDispatched(false);

      // Auto-fill form
      const oc = data.oc;
      const customer = data.customer || {};
      const shipTo = data.ship_to || {};
      // Build delivery address: prefer ship_to, fallback to customer address
      const shipToAddr = [shipTo.name, shipTo.address, shipTo.city, shipTo.state, shipTo.pincode].filter(Boolean).join(', ');
      const customerAddr = [customer.address || oc.customer?.address, customer.city || oc.customer?.city, customer.state || oc.customer?.state, customer.pincode || oc.customer?.pincode].filter(Boolean).join(', ');
      const deliveryAddr = shipToAddr || customerAddr;
      const customerName = oc.customer_name || (typeof oc.customer === 'object' ? oc.customer?.name : '') || customer.name || '';
      const customerId = oc.customer_id || (typeof oc.customer === 'object' ? oc.customer?.id : '') || customer.id || '';
      const ocBranch = data.branch || oc.branch || '';

      setForm(prev => ({
        ...prev,
        oc_reference: oc.id,
        oc_number: oc.oc_number || '',
        customer: customerId,
        customer_name: customerName,
        from_branch: ocBranch,
        to_location: deliveryAddr || '',
        delivery_address: deliveryAddr || '',
        items: data.remaining_items
          .filter(i => i.remaining_qty > 0)
          .map(i => ({ product: i.product, name: i.name, qty: i.remaining_qty, unit: i.unit })),
      }));
      setOcSearch(oc.oc_number || '');
      setShowOCDropdown(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load OC data');
      setLinkedOC(null);
      setOcData(null);
    }
    setOcLoading(false);
  };

  const selectOC = (oc) => {
    loadOCData(oc.id);
  };

  const clearOCLink = () => {
    setLinkedOC(null);
    setOcData(null);
    setOcSearch('');
    setFullyDispatched(false);
    setError('');
    setForm(prev => ({
      ...prev,
      oc_reference: '', oc_number: '', customer: '', customer_name: '', to_location: '', delivery_address: '',
      items: [{ product: '', name: '', qty: 0, unit: 'PCS' }],
    }));
  };

  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { product: '', name: '', qty: 0, unit: 'PCS' }] }));
  const removeItem = (i) => setForm(f => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));
  const updateItem = (i, field, val) => setForm(f => ({ ...f, items: f.items.map((it, idx) => idx === i ? { ...it, [field]: val } : it) }));

  const handleSubmit = async () => {
    if (submittingRef.current) return; // Sync guard: prevents double-click duplicate dispatches
    if (fullyDispatched) return setError('Cannot create dispatch — all items fully dispatched');
    if (!form.customer_name.trim()) return setError('Customer name is required');
    if (!form.items.length || !form.items[0].name) return setError('At least one item is required');
    if (!form.transporter_name.trim()) return setError('Transporter name is required');
    if (!form.vehicle_no.trim()) return setError('Vehicle number is required');
    if (!form.driver_name.trim()) return setError('Driver name is required');
    if (!form.driver_phone.trim()) return setError('Driver phone is required');
    submittingRef.current = true;
    try {
      setSubmitting(true);
      setError('');
      const restFreight = Math.max(0, (parseFloat(form.total_freight) || 0) - (parseFloat(form.advance_freight) || 0));
      const submitData = {
        ...form,
        items: form.items.map(it => ({ ...it, qty: Number(it.qty) })),
        attachments: attachments.map(a => a.path),
        total_freight: parseFloat(form.total_freight) || 0,
        advance_freight: parseFloat(form.advance_freight) || 0,
        rest_freight: restFreight,
      };
      await dispatchAPI.create(submitData);
      toast.success('Dispatch created successfully');
      onBack();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create dispatch');
    } finally { setSubmitting(false); submittingRef.current = false; }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const result = await fileAPI.upload(file, 'dispatch', 'new');
      setAttachments(prev => [...prev, { name: file.name, path: result.path || result.file_path }]);
    } catch (err) { console.error('Upload failed:', err); }
    fileRef.current.value = '';
  };

  return (
    <div className="p-5 lg:p-6">
      <button onClick={onBack} className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800 mb-4" data-testid="back-btn">
        <ArrowLeft className="w-3.5 h-3.5" /> Back
      </button>
      <h1 className="text-xl font-bold text-gray-900 mb-1" style={{ fontFamily: 'DM Sans' }}>New Dispatch Note</h1>
      <p className="text-xs text-gray-500 mb-5">Create a shipment dispatch</p>

      {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-600 flex items-center gap-2"><AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" /> {error}</div>}

      {/* OC Lookup Section */}
      <div className="bg-white rounded-xl border border-gray-100 p-4 mb-5">
        <div className="flex items-center gap-2 mb-3">
          <Link2 className="w-4 h-4 text-[#24549c]" />
          <h3 className="text-xs font-bold text-gray-900">Link to Order Confirmation</h3>
          <span className="text-[10px] text-gray-400">(auto-fills customer, items & address)</span>
        </div>

        {linkedOC ? (
          <div className="flex items-center justify-between p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                <CheckCircle className="w-4 h-4 text-emerald-600" />
              </div>
              <div>
                <p className="text-xs font-bold text-gray-900">{linkedOC.oc_number}</p>
                <p className="text-[10px] text-gray-500">{linkedOC.customer_name} — {linkedOC.status}</p>
              </div>
              {ocData && (
                <div className="ml-4 text-[10px] text-gray-500">
                  <span className="font-semibold text-gray-700">{ocData.remaining_items.filter(i => i.remaining_qty > 0).length}</span> items to dispatch
                  {ocData.past_dispatches > 0 && <span className="ml-2 text-amber-600">({ocData.past_dispatches} prior dispatch{ocData.past_dispatches > 1 ? 'es' : ''})</span>}
                </div>
              )}
            </div>
            <button onClick={clearOCLink} className="p-1.5 rounded-lg hover:bg-emerald-100" data-testid="clear-oc-link">
              <X className="w-3.5 h-3.5 text-gray-500" />
            </button>
          </div>
        ) : (
          <div className="relative" ref={ocDropdownRef}>
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <input
              value={ocSearch} onChange={e => setOcSearch(e.target.value)}
              onFocus={() => ocResults.length > 0 && setShowOCDropdown(true)}
              placeholder="Search OC number or customer name..."
              className="w-full pl-9 pr-10 py-2.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-300"
              data-testid="oc-search-input"
            />
            {ocSearching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 animate-spin text-gray-400" />}
            {ocLoading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 animate-spin text-blue-500" />}

            {showOCDropdown && ocResults.length > 0 && (
              <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                {ocResults.map(oc => (
                  <button key={oc.id} onClick={() => selectOC(oc)} data-testid={`oc-option-${oc.id}`}
                    className="w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-blue-50 transition-colors border-b border-gray-50 last:border-0">
                    <div>
                      <p className="text-xs font-semibold text-gray-900">{oc.oc_number}</p>
                      <p className="text-[10px] text-gray-500">{oc.customer_name}</p>
                    </div>
                    <div className="text-right">
                      <span className={`inline-flex px-2 py-0.5 text-[9px] font-semibold rounded-full ${
                        oc.status === 'Ready to Dispatch' ? 'bg-amber-100 text-amber-700' :
                        oc.status === 'Partially Dispatched' ? 'bg-blue-100 text-blue-700' :
                        'bg-green-100 text-green-700'
                      }`}>{oc.status}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
            {showOCDropdown && ocSearch && !ocSearching && ocResults.length === 0 && (
              <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg p-4 text-center">
                <p className="text-xs text-gray-500">No eligible OCs found</p>
                <p className="text-[10px] text-gray-400 mt-0.5">Only "Ready to Dispatch" and above are shown</p>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <div className="space-y-4">
          {/* Reference & Customer */}
          <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-3">
            <h3 className="text-xs font-bold text-gray-900">Reference & Customer</h3>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="block text-[11px] font-medium text-gray-700 mb-1">OC Number</label>
                <input value={form.oc_number} onChange={e => setForm({ ...form, oc_number: e.target.value })} placeholder="OC-00001" data-testid="dsp-oc-number"
                  className={`w-full px-3 py-2 text-xs border border-gray-200 rounded-lg ${linkedOC ? 'bg-gray-50 text-gray-500' : ''}`} readOnly={!!linkedOC} /></div>
              <div><label className="block text-[11px] font-medium text-gray-700 mb-1">Production Ref</label>
                <input value={form.production_number} onChange={e => setForm({ ...form, production_number: e.target.value })} placeholder="PRD-00001"
                  className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" /></div>
            </div>
            <div><label className="block text-[11px] font-medium text-gray-700 mb-1">Customer Name *</label>
              <input value={form.customer_name} onChange={e => setForm({ ...form, customer_name: e.target.value })} data-testid="dsp-customer"
                className={`w-full px-3 py-2 text-xs border border-gray-200 rounded-lg ${linkedOC ? 'bg-gray-50' : ''}`} readOnly={!!linkedOC} /></div>
            <div><label className="block text-[11px] font-medium text-gray-700 mb-1">Delivery Address</label>
              <input value={form.to_location} onChange={e => setForm({ ...form, to_location: e.target.value })} data-testid="dsp-to-location"
                className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" /></div>
            <div><label className="block text-[11px] font-medium text-gray-700 mb-1">From Branch</label>
              <select value={form.from_branch} onChange={e => setForm({ ...form, from_branch: e.target.value })}
                className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg">
                <option value="">Select branch</option>
                {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
          </div>

          {/* Transport Details */}
          <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-3">
            <h3 className="text-xs font-bold text-gray-900">Transport Details</h3>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="block text-[11px] font-medium text-gray-700 mb-1">Vehicle No <span className="text-red-500">*</span></label>
                <input value={form.vehicle_no} onChange={e => setForm({ ...form, vehicle_no: e.target.value })} placeholder="MH-01-AB-1234" data-testid="dsp-vehicle"
                  className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" /></div>
              <div><label className="block text-[11px] font-medium text-gray-700 mb-1">Transporter <span className="text-red-500">*</span></label>
                <input value={form.transporter_name} onChange={e => setForm({ ...form, transporter_name: e.target.value })} data-testid="dsp-transporter"
                  className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="block text-[11px] font-medium text-gray-700 mb-1">Driver Name <span className="text-red-500">*</span></label>
                <input value={form.driver_name} onChange={e => setForm({ ...form, driver_name: e.target.value })} data-testid="dsp-driver-name"
                  className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" /></div>
              <div><label className="block text-[11px] font-medium text-gray-700 mb-1">Driver Phone <span className="text-red-500">*</span></label>
                <input value={form.driver_phone} onChange={e => setForm({ ...form, driver_phone: e.target.value })} data-testid="dsp-driver-phone"
                  className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="block text-[11px] font-medium text-gray-700 mb-1">LR Number</label>
                <input value={form.lr_number} onChange={e => setForm({ ...form, lr_number: e.target.value })} data-testid="dsp-lr"
                  className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" /></div>
              <div><label className="block text-[11px] font-medium text-gray-700 mb-1">E-Way Bill</label>
                <input value={form.eway_bill} onChange={e => setForm({ ...form, eway_bill: e.target.value })} data-testid="dsp-eway"
                  className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="block text-[11px] font-medium text-gray-700 mb-1">Dispatch Date</label>
                <input type="date" value={form.dispatch_date} onChange={e => setForm({ ...form, dispatch_date: e.target.value })}
                  className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" /></div>
              <div><label className="block text-[11px] font-medium text-gray-700 mb-1">Expected Delivery</label>
                <input type="date" value={form.expected_delivery} onChange={e => setForm({ ...form, expected_delivery: e.target.value })}
                  className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" /></div>
            </div>
          </div>

          {/* Freight Details */}
          <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-3">
            <h3 className="text-xs font-bold text-gray-900">Freight Details</h3>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-[11px] font-medium text-gray-700 mb-1">Total Freight</label>
                <input type="number" min="0" step="0.01" value={form.total_freight} data-testid="dsp-total-freight"
                  onChange={e => {
                    const total = e.target.value;
                    const rest = Math.max(0, (parseFloat(total) || 0) - (parseFloat(form.advance_freight) || 0));
                    setForm({ ...form, total_freight: total, rest_freight: rest });
                  }}
                  placeholder="0.00" className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" />
              </div>
              <div>
                <label className="block text-[11px] font-medium text-gray-700 mb-1">Advance Freight</label>
                <input type="number" min="0" step="0.01" value={form.advance_freight} data-testid="dsp-advance-freight"
                  onChange={e => {
                    const advance = e.target.value;
                    const rest = Math.max(0, (parseFloat(form.total_freight) || 0) - (parseFloat(advance) || 0));
                    setForm({ ...form, advance_freight: advance, rest_freight: rest });
                  }}
                  placeholder="0.00" className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" />
              </div>
              <div>
                <label className="block text-[11px] font-medium text-gray-700 mb-1">Rest Freight</label>
                <input type="number" value={form.rest_freight} readOnly data-testid="dsp-rest-freight"
                  className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg bg-gray-50 font-semibold" />
              </div>
            </div>
            <p className="text-[10px] text-gray-400">Rest Freight = Total Freight - Advance Freight</p>
          </div>
        </div>

        <div className="space-y-4">
          {/* Items — with remaining qty info when linked to OC */}
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-bold text-gray-900">Dispatch Items</h3>
              <button onClick={addItem} className="text-[10px] font-semibold text-blue-600 hover:underline">+ Add Item</button>
            </div>

            {/* Remaining qty header when linked to OC */}
            {linkedOC && ocData && (
              <div className="mb-3 p-2 bg-blue-50 rounded-lg border border-blue-100">
                <p className="text-[10px] font-semibold text-blue-700">Items auto-filled with remaining quantities from {linkedOC.oc_number}</p>
              </div>
            )}

            <div className="space-y-2">
              {form.items.map((item, i) => (
                <div key={i} className="flex gap-2 items-end">
                  <div className="flex-1"><label className="block text-[10px] text-gray-500 mb-0.5">Item</label>
                    <input value={item.name} onChange={e => updateItem(i, 'name', e.target.value)} placeholder="Product name"
                      className={`w-full px-2 py-1.5 text-[11px] border border-gray-200 rounded-lg ${linkedOC ? 'bg-gray-50' : ''}`} readOnly={!!linkedOC} /></div>
                  <div className="w-20"><label className="block text-[10px] text-gray-500 mb-0.5">Qty</label>
                    <input type="number" value={item.qty} onChange={e => updateItem(i, 'qty', e.target.value)}
                      className="w-full px-2 py-1.5 text-[11px] border border-gray-200 rounded-lg" /></div>
                  <div className="w-16"><label className="block text-[10px] text-gray-500 mb-0.5">Unit</label>
                    <input value={item.unit} onChange={e => updateItem(i, 'unit', e.target.value)}
                      className="w-full px-2 py-1.5 text-[11px] border border-gray-200 rounded-lg" /></div>
                  {form.items.length > 1 && !linkedOC && (
                    <button onClick={() => removeItem(i)} className="p-1.5 text-red-400 hover:text-red-600"><X className="w-3 h-3" /></button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <h3 className="text-xs font-bold text-gray-900 mb-2">Notes</h3>
            <textarea value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })} rows={3} placeholder="Any special instructions..."
              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg resize-none" data-testid="dsp-notes" />
          </div>

          {/* Attachments */}
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <h3 className="text-xs font-bold text-gray-900 mb-2">Documents</h3>
            <input type="file" ref={fileRef} className="hidden" onChange={handleFileUpload} />
            <button onClick={() => fileRef.current.click()} className="flex items-center gap-1.5 px-3 py-2 text-xs border border-dashed border-gray-300 rounded-lg hover:border-blue-400 text-gray-500 mb-2">
              <Upload className="w-3 h-3" /> Attach Document
            </button>
            {attachments.map((a, i) => (
              <div key={i} className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 rounded-lg text-xs mb-1">
                <FileText className="w-3 h-3 text-blue-500" /><span className="text-gray-700">{a.name}</span>
                <button onClick={() => setAttachments(p => p.filter((_, idx) => idx !== i))} className="ml-auto text-gray-400 hover:text-red-500"><X className="w-3 h-3" /></button>
              </div>
            ))}
          </div>

          <button onClick={handleSubmit} disabled={submitting || fullyDispatched} data-testid="create-dispatch-btn"
            className="w-full px-4 py-2.5 text-xs font-semibold text-white rounded-lg disabled:opacity-50 flex items-center justify-center gap-2" style={{ backgroundColor: '#24549c' }}>
            {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Truck className="w-3.5 h-3.5" />}
            Create Dispatch Note
          </button>
        </div>
      </div>
    </div>
  );
};

// ==================== DISPATCH DETAIL VIEW ====================

const EXTRA_CHARGE_TYPES = ['Unloading', 'Demurrage', 'Diversion', 'Toll/Octroi', 'Loading at Destination', 'Detention', 'Other'];

const ExtraChargesSection = ({ dispatch, onUpdate }) => {
  const [editing, setEditing] = useState(false);
  const [charges, setCharges] = useState(dispatch?.extra_charges || []);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setCharges(dispatch?.extra_charges || []); }, [dispatch]);

  const addCharge = () => setCharges(prev => [...prev, { id: '', charge_type: 'Unloading', amount: 0, notes: '', date: new Date().toISOString().split('T')[0] }]);
  const removeCharge = (idx) => setCharges(prev => prev.filter((_, i) => i !== idx));
  const updateCharge = (idx, field, val) => setCharges(prev => prev.map((c, i) => i === idx ? { ...c, [field]: val } : c));

  const saveCharges = async () => {
    const valid = charges.filter(c => c.amount > 0);
    try {
      setSaving(true);
      const updated = await dispatchAPI.updateExtraCharges(dispatch.id, valid);
      onUpdate(updated);
      setEditing(false);
      toast.success('Extra charges saved');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const totalExtra = charges.reduce((a, c) => a + (parseFloat(c.amount) || 0), 0);

  return (
    <div className="mt-3 pt-3 border-t-2 border-dashed border-gray-200" data-testid="extra-charges-section">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-[10px] font-bold text-red-600 uppercase tracking-wider">Extra Charges (Paid to Transporter — Not in Freight)</h4>
        {!editing && (
          <button onClick={() => setEditing(true)} data-testid="edit-extra-charges-btn"
            className="flex items-center gap-1 px-2 py-1 text-[9px] font-semibold text-red-600 bg-red-50 rounded hover:bg-red-100">
            <Pencil className="w-2.5 h-2.5" /> {charges.length > 0 ? 'Edit' : 'Add Charges'}
          </button>
        )}
      </div>

      {editing ? (
        <div className="space-y-2">
          {charges.map((c, idx) => (
            <div key={idx} className="flex items-center gap-2" data-testid={`extra-charge-edit-${idx}`}>
              <select value={c.charge_type} onChange={e => updateCharge(idx, 'charge_type', e.target.value)}
                className="px-2 py-1.5 text-[11px] border border-gray-200 rounded-lg w-40">
                {EXTRA_CHARGE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <input type="number" min="0" step="0.01" value={c.amount} onChange={e => updateCharge(idx, 'amount', parseFloat(e.target.value) || 0)}
                placeholder="Amount" className="px-2 py-1.5 text-[11px] border border-gray-200 rounded-lg w-28 text-right" data-testid={`extra-charge-amount-${idx}`} />
              <input type="date" value={c.date || ''} onChange={e => updateCharge(idx, 'date', e.target.value)}
                className="px-2 py-1.5 text-[11px] border border-gray-200 rounded-lg w-32" />
              <input value={c.notes || ''} onChange={e => updateCharge(idx, 'notes', e.target.value)}
                placeholder="Notes" className="px-2 py-1.5 text-[11px] border border-gray-200 rounded-lg flex-1" />
              <button onClick={() => removeCharge(idx)} className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          <button onClick={addCharge} className="text-[10px] font-semibold text-blue-600 hover:text-blue-800" data-testid="add-extra-charge">+ Add Charge</button>
          <div className="flex items-center gap-2 pt-1">
            <span className="text-[10px] font-bold text-gray-500">Total Extra: <span className="text-red-600">INR {totalExtra.toLocaleString('en-IN')}</span></span>
            <span className="ml-auto" />
            <button onClick={saveCharges} disabled={saving} data-testid="save-extra-charges"
              className="px-3 py-1.5 text-[10px] font-semibold text-white rounded-lg disabled:opacity-50" style={{ backgroundColor: '#24549c' }}>
              {saving ? <Loader2 className="w-3 h-3 animate-spin inline" /> : null} Save
            </button>
            <button onClick={() => { setCharges(dispatch?.extra_charges || []); setEditing(false); }}
              className="px-3 py-1.5 text-[10px] font-semibold text-gray-600 bg-gray-100 rounded-lg">Cancel</button>
          </div>
        </div>
      ) : charges.length > 0 ? (
        <div className="space-y-1.5">
          {charges.map((c, idx) => (
            <div key={idx} className="flex items-center justify-between text-[11px] py-1.5 px-2 bg-red-50/30 rounded">
              <div className="flex items-center gap-2">
                <span className={`px-1.5 py-0.5 text-[9px] font-semibold rounded-full ${
                  c.charge_type === 'Demurrage' ? 'bg-red-100 text-red-700' :
                  c.charge_type === 'Unloading' ? 'bg-blue-100 text-blue-700' :
                  c.charge_type === 'Diversion' ? 'bg-amber-100 text-amber-700' :
                  'bg-gray-100 text-gray-700'
                }`}>{c.charge_type}</span>
                {c.notes && <span className="text-gray-500">{c.notes}</span>}
                {c.date && <span className="text-gray-400 text-[10px]">{c.date}</span>}
              </div>
              <span className="font-bold text-red-600">INR {(c.amount || 0).toLocaleString('en-IN')}</span>
            </div>
          ))}
          <div className="flex justify-between items-center pt-2 border-t border-red-100 text-[11px]">
            <span className="font-bold text-gray-500">Total Extra Charges</span>
            <span className="font-bold text-red-600">INR {totalExtra.toLocaleString('en-IN')}</span>
          </div>
          {dispatch.total_freight > 0 && (
            <div className="flex justify-between items-center text-[11px]">
              <span className="font-bold text-gray-500">Total Logistics (Freight + Extra)</span>
              <span className="font-bold text-gray-900">INR {((dispatch.total_freight || 0) + totalExtra).toLocaleString('en-IN')}</span>
            </div>
          )}
        </div>
      ) : (
        <p className="text-[10px] text-gray-400 italic">No extra charges added.</p>
      )}
    </div>
  );
};

const DispatchDetail = ({ dispatchId, onBack }) => {
  const [dispatch, setDispatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState({});

  const [uploading, setUploading] = useState(false);

  // Edit Vehicle Number dialog (Awaiting Invoice only)
  const [showEditVehicleDialog, setShowEditVehicleDialog] = useState(false);
  const [editVehicleValue, setEditVehicleValue] = useState('');
  const [savingVehicle, setSavingVehicle] = useState(false);
  const _currentUser = JSON.parse(localStorage.getItem('prismoline_user') || '{}');
  const isAdmin = _currentUser.role === 'Admin' || _currentUser.role === 'Manager';

  // Cancel Dispatch dialog (Admin/Manager, any non-cancelled dispatch)
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);

  const handleCancelDispatch = async () => {
    const reason = cancelReason.trim();
    if (!reason) { toast.error('A cancellation reason is required'); return; }
    try {
      setCancelling(true);
      const updated = await dispatchAPI.cancel(dispatchId, reason);
      setDispatch(updated);
      setShowCancelDialog(false);
      setCancelReason('');
      toast.success('Dispatch cancelled — stock has been restored');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel dispatch');
    } finally {
      setCancelling(false);
    }
  };

  const handleSaveDispatchVehicleNumber = async () => {
    const newVeh = editVehicleValue.trim().toUpperCase();
    if (!newVeh) { toast.error('Vehicle number is required'); return; }
    if (newVeh === (dispatch?.vehicle_no || '').toUpperCase()) {
      toast.error('Vehicle number unchanged');
      return;
    }
    try {
      setSavingVehicle(true);
      const updated = await dispatchAPI.updateVehicleNumber(dispatchId, newVeh);
      setDispatch(updated);
      toast.success(`Vehicle number updated to ${newVeh}`);
      setShowEditVehicleDialog(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update vehicle number');
    } finally {
      setSavingVehicle(false);
    }
  };

  useEffect(() => {
    dispatchAPI.getById(dispatchId).then(d => { setDispatch(d); setLoading(false); }).catch(() => setLoading(false));
  }, [dispatchId]);

  const isPending = dispatch?.status === 'Pending';
  // Buyer-arranged freight: fields are optional (Ex-Works or To Pay).
  // Uses startsWith to tolerate dropdown label suffixes like
  // "Ex-Works (buyer to arrange pickup)" or "To Pay (freight paid by buyer)".
  const _freightLower = (dispatch?.freight_terms || '').trim().toLowerCase();
  const isBuyerArranged = _freightLower.startsWith('ex-works')
    || _freightLower.startsWith('exw')
    || _freightLower.startsWith('ex works')
    || _freightLower.startsWith('to pay');

  // Check if required fields for "In Transit" are filled (skip for buyer-arranged freight)
  const canMarkInTransit = (d) => {
    if (!d) return false;
    if (isBuyerArranged) return true; // Buyer arranges transport — no transport/freight required
    return (
      (d.transporter_name || '').trim() !== '' &&
      (d.driver_name || '').trim() !== '' &&
      (d.driver_phone || '').trim() !== '' &&
      (d.total_freight || 0) > 0 &&
      d.advance_freight !== null && d.advance_freight !== undefined
    );
  };

  const getMissingFields = (d) => {
    if (!d || isBuyerArranged) return []; // Buyer-arranged: nothing required
    const missing = [];
    if (!(d.transporter_name || '').trim()) missing.push('Transporter');
    if (!(d.driver_name || '').trim()) missing.push('Driver Name');
    if (!(d.driver_phone || '').trim()) missing.push('Driver Contact No.');
    if (!(d.total_freight > 0)) missing.push('Total Freight');
    if (d.advance_freight === null || d.advance_freight === undefined) missing.push('Advance');
    return missing;
  };

  const startEditing = () => {
    setEditForm({
      transporter_name: dispatch.transporter_name || '',
      driver_name: dispatch.driver_name || '',
      driver_phone: dispatch.driver_phone || '',
      lr_number: dispatch.lr_number || '',
      eway_bill: dispatch.eway_bill || '',
      delivery_address: dispatch.to_location || dispatch.delivery_address || '',
      expected_delivery: dispatch.expected_delivery || '',
      total_freight: dispatch.total_freight || '',
      advance_freight: dispatch.advance_freight || '',
    });
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
    setEditForm({});
  };

  const saveTransportDetails = async () => {
    // For buyer-arranged freight (Ex-Works, To Pay), transport/freight fields are optional
    if (!isBuyerArranged) {
      if (!editForm.transporter_name?.trim()) return toast.error('Transporter name is required');
      if (!editForm.driver_name?.trim()) return toast.error('Driver name is required');
      if (!editForm.driver_phone?.trim()) return toast.error('Driver contact no. is required');
      if (!(parseFloat(editForm.total_freight) > 0)) return toast.error('Total Freight must be greater than 0');
      if (editForm.advance_freight === '' || editForm.advance_freight === null || editForm.advance_freight === undefined) return toast.error('Advance amount is required');
    }
    const totalF = parseFloat(editForm.total_freight) || 0;
    const advanceF = parseFloat(editForm.advance_freight) || 0;
    if (advanceF > totalF) return toast.error('Advance cannot exceed Total Freight');

    try {
      setSaving(true);
      const payload = {
        transporter_name: editForm.transporter_name.trim(),
        driver_name: editForm.driver_name.trim(),
        driver_phone: editForm.driver_phone.trim(),
        lr_number: editForm.lr_number?.trim() || '',
        eway_bill: editForm.eway_bill?.trim() || '',
        to_location: editForm.delivery_address?.trim() || '',
        delivery_address: editForm.delivery_address?.trim() || '',
        expected_delivery: editForm.expected_delivery || '',
        total_freight: totalF,
        advance_freight: advanceF,
        rest_freight: Math.max(0, totalF - advanceF),
      };
      const updated = await dispatchAPI.update(dispatchId, payload);
      setDispatch(updated);
      setEditing(false);
      toast.success('Transport & freight details saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save details');
    } finally { setSaving(false); }
  };

  const updateStatus = async (status) => {
    try {
      setUpdating(true);
      const updated = await dispatchAPI.update(dispatchId, { status });
      setDispatch(updated);
      toast.success(`Status updated to ${status}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    } finally { setUpdating(false); }
  };

  const handleAttachmentUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploading(true);
      const uploaded = await fileAPI.upload(file, 'dispatch', dispatchId);
      const url = uploaded.url || uploaded.file_url || uploaded.path;
      if (url) {
        const currentAttachments = dispatch.attachments || [];
        const updated = await dispatchAPI.update(dispatchId, { attachments: [...currentAttachments, url] });
        setDispatch(updated);
        toast.success('File uploaded successfully');
      }
    } catch (err) {
      toast.error('Upload failed');
    } finally { setUploading(false); e.target.value = ''; }
  };

  const downloadPDF = () => {
    if (!dispatch) return;
    const doc = new jsPDF();
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text('DISPATCH NOTE', 105, 20, { align: 'center' });
    doc.setFontSize(9);
    doc.text('SHARDA INFRASOLUTIONS PVT. LTD.', 105, 28, { align: 'center' });
    doc.setDrawColor(36, 84, 156);
    doc.setLineWidth(0.5);
    doc.line(15, 32, 195, 32);

    let y = 42;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    const addRow = (label, value) => { doc.setFont('helvetica', 'bold'); doc.text(label, 15, y); doc.setFont('helvetica', 'normal'); doc.text(String(value || '-'), 70, y); y += 6; };

    addRow('Dispatch #:', dispatch.dispatch_number);
    addRow('Date:', dispatch.dispatch_date);
    addRow('Customer:', dispatch.customer_name);
    addRow('Delivery To:', dispatch.to_location);
    addRow('Vehicle No:', dispatch.vehicle_no);
    addRow('Transporter:', dispatch.transporter_name);
    addRow('Driver:', `${dispatch.driver_name || ''} ${dispatch.driver_phone ? '(' + dispatch.driver_phone + ')' : ''}`);
    addRow('LR Number:', dispatch.lr_number);
    addRow('E-Way Bill:', dispatch.eway_bill);
    if (dispatch.oc_number) addRow('OC Reference:', dispatch.oc_number);
    if (dispatch.production_number) addRow('Production Ref:', dispatch.production_number);

    // Ship To Address from OC
    if (dispatch.ship_to_address) {
      const sta = dispatch.ship_to_address;
      const shipToText = [sta.name, sta.address, sta.city, sta.state, sta.pincode].filter(Boolean).join(', ');
      if (shipToText) addRow('Ship To:', shipToText);
      if (sta.contact_person) addRow('Ship To Contact:', `${sta.contact_person}${sta.phone ? ' (' + sta.phone + ')' : ''}`);
    }
    if (dispatch.freight_terms) addRow('Freight Terms:', dispatch.freight_terms);

    if (dispatch.total_freight > 0 || dispatch.advance_freight > 0) {
      y += 4;
      addRow('Total Freight:', `INR ${(dispatch.total_freight || 0).toLocaleString('en-IN')}`);
      addRow('Advance Freight:', `INR ${(dispatch.advance_freight || 0).toLocaleString('en-IN')}`);
      addRow('Rest Freight:', `INR ${(dispatch.rest_freight || 0).toLocaleString('en-IN')}`);
    }

    y += 6;
    doc.setFont('helvetica', 'bold');
    doc.text('ITEMS', 15, y); y += 4;

    // Table header
    doc.setFillColor(36, 84, 156);
    doc.rect(15, y - 3, 180, 7, 'F');
    doc.setTextColor(255); doc.setFontSize(8);
    doc.text('#', 18, y + 2); doc.text('Product', 30, y + 2); doc.text('Quantity', 140, y + 2); doc.text('Unit', 170, y + 2);
    y += 7;

    doc.setFont('helvetica', 'normal'); doc.setTextColor(0);
    (dispatch.items || []).forEach((item, i) => {
      if (i % 2 === 0) { doc.setFillColor(248, 249, 252); doc.rect(15, y - 3, 180, 6, 'F'); }
      doc.setTextColor(0);
      doc.text(String(i + 1), 18, y);
      doc.text(item.name || item.product || '', 30, y);
      doc.text(String(item.qty || 0), 140, y);
      doc.text(item.unit || '', 170, y);
      y += 6;
      doc.setDrawColor(220); doc.line(15, y - 3, 195, y - 3);
    });

    if (dispatch.notes) { y += 6; doc.setFont('helvetica', 'bold'); doc.text('Notes:', 15, y); y += 5; doc.setFont('helvetica', 'normal'); doc.text(dispatch.notes, 15, y); }

    doc.save(`${dispatch.dispatch_number}.pdf`);
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>;
  if (!dispatch) return <div className="p-5"><button onClick={onBack} className="text-xs text-gray-500"><ArrowLeft className="inline w-3 h-3" /> Back</button><p className="mt-4">Not found</p></div>;

  const sc = statusConfig[dispatch.status] || statusConfig['Pending'];
  const transitAllowed = canMarkInTransit(dispatch);
  const missingFields = getMissingFields(dispatch);

  const statusActions = {
    'Awaiting Invoice': [],
    'Pending': [{ label: 'Mark In Transit', status: 'In Transit', color: '#3b82f6', disabled: !transitAllowed }],
    'In Transit': [{ label: 'Mark Delivered', status: 'Delivered', color: '#059669' }],
  };

  // For buyer-arranged freight in Pending: show both In Transit and Delivered directly
  if (isBuyerArranged && dispatch?.status === 'Pending') {
    statusActions['Pending'] = [
      { label: 'Mark In Transit', status: 'In Transit', color: '#3b82f6', disabled: false },
      { label: 'Mark Delivered', status: 'Delivered', color: '#059669', disabled: false },
    ];
  }

  const restFreightCalc = Math.max(0, (parseFloat(editForm.total_freight) || 0) - (parseFloat(editForm.advance_freight) || 0));

  return (
    <div className="p-5 lg:p-6">
      <button onClick={onBack} className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800 mb-4" data-testid="back-btn">
        <ArrowLeft className="w-3.5 h-3.5" /> Back
      </button>

      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-100 p-5 mb-5">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h1 className="text-lg font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>{dispatch.dispatch_number}</h1>
              <span className={`inline-flex px-2.5 py-0.5 text-[11px] font-semibold rounded-full border ${sc.bg} ${sc.text} ${sc.border}`}>{dispatch.status}</span>
            </div>
            <p className="text-sm text-gray-700">{dispatch.customer_name}</p>
            <div className="flex items-center gap-3 text-[11px] text-gray-500 mt-1">
              <span>Date: {dispatch.dispatch_date}</span>
              {dispatch.oc_number && <span>OC: <span className="font-semibold">{dispatch.oc_number}</span></span>}
              {dispatch.production_number && <span>PRD: <span className="font-semibold">{dispatch.production_number}</span></span>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={downloadPDF} data-testid="download-pdf-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">
              <Download className="w-3 h-3" /> PDF
            </button>
            <button onClick={() => { downloadPDF(); window.print(); }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">
              <Printer className="w-3 h-3" /> Print
            </button>
            {(statusActions[dispatch.status] || []).map(a => (
              <div key={a.status} className="relative group">
                <button onClick={() => updateStatus(a.status)} disabled={updating || a.disabled} data-testid={`status-${a.status}`}
                  className="px-4 py-1.5 text-xs font-semibold text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed" style={{ backgroundColor: a.color }}>
                  {updating ? <Loader2 className="w-3 h-3 animate-spin" /> : a.label}
                </button>
                {a.disabled && missingFields.length > 0 && (
                  <div className="absolute right-0 top-full mt-1 z-30 hidden group-hover:block w-64 p-3 bg-gray-900 text-white text-[10px] rounded-lg shadow-xl">
                    <p className="font-semibold mb-1">Fill required details first:</p>
                    <ul className="list-disc pl-3 space-y-0.5">
                      {missingFields.map(f => <li key={f}>{f}</li>)}
                    </ul>
                    <p className="mt-1.5 text-gray-300">Click "Edit Transport & Freight" below to fill.</p>
                  </div>
                )}
              </div>
            ))}
            {isAdmin && dispatch.status !== 'Cancelled' && (
              <button onClick={() => { setCancelReason(''); setShowCancelDialog(true); }} disabled={updating}
                data-testid="cancel-dispatch-btn"
                className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50">
                <Ban className="w-3.5 h-3.5" /> Cancel Dispatch
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Missing fields warning banner for Pending status (skip when buyer arranges freight) */}
      {isPending && !isBuyerArranged && missingFields.length > 0 && !editing && (
        <div className="mb-5 p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3" data-testid="missing-fields-banner">
          <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-xs font-semibold text-amber-800">Transport & Freight details required before marking "In Transit"</p>
            <p className="text-[10px] text-amber-600 mt-0.5">Missing: {missingFields.join(', ')}</p>
          </div>
          <button onClick={startEditing} data-testid="edit-transport-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-amber-600 rounded-lg hover:bg-amber-700 flex-shrink-0">
            <Pencil className="w-3 h-3" /> Fill Details
          </button>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-5">
        {/* Ship To & Freight Terms (from OC) */}
        {(dispatch.ship_to_address || dispatch.freight_terms) && (
          <div className="lg:col-span-2 bg-white rounded-xl border border-gray-100 p-4 text-xs" data-testid="dispatch-ship-to-section">
            <div className="grid md:grid-cols-2 gap-4">
              {dispatch.ship_to_address && Object.values(dispatch.ship_to_address).some(Boolean) && (
                <div>
                  <h4 className="text-[10px] font-bold text-gray-500 uppercase mb-2">Ship To Address (from OC)</h4>
                  <div className="bg-gray-50 rounded-lg p-3 space-y-0.5">
                    {dispatch.ship_to_address.name && <p className="font-semibold text-gray-900">{dispatch.ship_to_address.name}</p>}
                    {dispatch.ship_to_address.address && <p className="text-gray-600">{dispatch.ship_to_address.address}</p>}
                    <p className="text-gray-600">{[dispatch.ship_to_address.city, dispatch.ship_to_address.state, dispatch.ship_to_address.pincode].filter(Boolean).join(', ')}</p>
                    {dispatch.ship_to_address.contact_person && <p className="text-gray-500 mt-1">Contact: {dispatch.ship_to_address.contact_person} {dispatch.ship_to_address.phone && `(${dispatch.ship_to_address.phone})`}</p>}
                  </div>
                </div>
              )}
              {dispatch.freight_terms && (
                <div>
                  <h4 className="text-[10px] font-bold text-gray-500 uppercase mb-2">Freight Terms (from OC)</h4>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="font-semibold text-gray-900" data-testid="dispatch-freight-terms">{dispatch.freight_terms}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Transport Info — Edit or Read mode */}
        <div className="bg-white rounded-xl border border-gray-100 p-4 text-xs">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-gray-900">Transport Details</h3>
            {isPending && !editing && (
              <button onClick={startEditing} data-testid="edit-transport-details-btn"
                className="flex items-center gap-1 px-2.5 py-1 text-[10px] font-semibold text-[#24549c] bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
                <Pencil className="w-3 h-3" /> Edit
              </button>
            )}
          </div>

          {editing ? (
            /* ===== EDIT MODE ===== */
            <div className="space-y-3" data-testid="transport-edit-form">
              {isBuyerArranged && (
                <div className="p-2 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-[10px] font-semibold text-blue-700">Ex-Works: Transport & freight details are optional. Buyer arranges pickup.</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-medium text-gray-500 mb-1 flex items-center justify-between">
                    <span>Vehicle No.</span>
                    {(dispatch.status === 'Awaiting Invoice') && isAdmin && (
                      <button
                        type="button"
                        data-testid="dsp-edit-vehicle-btn"
                        onClick={() => { setEditVehicleValue(dispatch.vehicle_no || ''); setShowEditVehicleDialog(true); }}
                        title="Edit vehicle number (available until invoice is uploaded)"
                        className="text-[10px] font-semibold text-amber-700 hover:text-amber-900 underline normal-case"
                      >
                        Edit
                      </button>
                    )}
                  </label>
                  <input value={dispatch.vehicle_no || ''} disabled
                    className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed"
                    data-testid="edit-vehicle-no" />
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-gray-700 mb-1">Transporter {!isBuyerArranged && <span className="text-red-500">*</span>}</label>
                  <input value={editForm.transporter_name} onChange={e => setEditForm(f => ({ ...f, transporter_name: e.target.value }))}
                    placeholder="Transporter name"
                    className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-300"
                    data-testid="edit-transporter" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-medium text-gray-700 mb-1">Driver Name {!isBuyerArranged && <span className="text-red-500">*</span>}</label>
                  <input value={editForm.driver_name} onChange={e => setEditForm(f => ({ ...f, driver_name: e.target.value }))}
                    placeholder="Driver name"
                    className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-300"
                    data-testid="edit-driver-name" />
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-gray-700 mb-1">Driver Contact No. {!isBuyerArranged && <span className="text-red-500">*</span>}</label>
                  <input value={editForm.driver_phone} onChange={e => setEditForm(f => ({ ...f, driver_phone: e.target.value }))}
                    placeholder="Phone number"
                    className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-300"
                    data-testid="edit-driver-phone" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-medium text-gray-500 mb-1">LR Number</label>
                  <input value={editForm.lr_number} onChange={e => setEditForm(f => ({ ...f, lr_number: e.target.value }))}
                    placeholder="LR number"
                    className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-300"
                    data-testid="edit-lr-number" />
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-gray-500 mb-1">E-Way Bill</label>
                  <input value={editForm.eway_bill} onChange={e => setEditForm(f => ({ ...f, eway_bill: e.target.value }))}
                    placeholder="E-Way bill no."
                    className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-300"
                    data-testid="edit-eway-bill" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-medium text-gray-500 mb-1">Delivery Address</label>
                  <input value={editForm.delivery_address} onChange={e => setEditForm(f => ({ ...f, delivery_address: e.target.value }))}
                    placeholder="Delivery address"
                    className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-300"
                    data-testid="edit-delivery-address" />
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-gray-500 mb-1">Expected Delivery</label>
                  <input type="date" value={editForm.expected_delivery} onChange={e => setEditForm(f => ({ ...f, expected_delivery: e.target.value }))}
                    className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-300"
                    data-testid="edit-expected-delivery" />
                </div>
              </div>

              {/* Freight Section */}
              <div className="pt-3 mt-3 border-t border-gray-100">
                <h4 className="text-[10px] font-bold text-gray-500 uppercase mb-2">Freight Details {isBuyerArranged && <span className="text-[9px] font-normal text-blue-500">(Optional for Ex-Works)</span>}</h4>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-[10px] font-medium text-gray-700 mb-1">Total Freight {!isBuyerArranged && <span className="text-red-500">*</span>}</label>
                    <input type="number" min="0" step="0.01" value={editForm.total_freight}
                      onChange={e => setEditForm(f => ({ ...f, total_freight: e.target.value }))}
                      placeholder="0.00"
                      className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-300"
                      data-testid="edit-total-freight" />
                  </div>
                  <div>
                    <label className="block text-[10px] font-medium text-gray-700 mb-1">Advance {!isBuyerArranged && <span className="text-red-500">*</span>}</label>
                    <input type="number" min="0" step="0.01" value={editForm.advance_freight}
                      onChange={e => setEditForm(f => ({ ...f, advance_freight: e.target.value }))}
                      placeholder="0.00"
                      className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-300"
                      data-testid="edit-advance-freight" />
                  </div>
                  <div>
                    <label className="block text-[10px] font-medium text-gray-700 mb-1">Rest Amount</label>
                    <input type="number" value={restFreightCalc} readOnly
                      className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg bg-gray-50 font-semibold text-amber-700"
                      data-testid="edit-rest-freight" />
                  </div>
                </div>
                <p className="text-[9px] text-gray-400 mt-1">Rest = Total Freight - Advance</p>
              </div>

              {/* Save / Cancel */}
              <div className="flex items-center gap-2 pt-2">
                <button onClick={saveTransportDetails} disabled={saving} data-testid="save-transport-btn"
                  className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white rounded-lg disabled:opacity-50" style={{ backgroundColor: '#24549c' }}>
                  {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save Details
                </button>
                <button onClick={cancelEditing} disabled={saving} data-testid="cancel-edit-btn"
                  className="px-4 py-2 text-xs font-semibold text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200">
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            /* ===== READ MODE ===== */
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <div><p className="text-gray-500">Vehicle</p><p className="font-semibold">{dispatch.vehicle_no || '-'}</p></div>
                <div><p className="text-gray-500">Transporter</p><p className={`font-semibold ${isPending && !dispatch.transporter_name ? 'text-red-400 italic' : ''}`}>{dispatch.transporter_name || (isPending ? 'Not filled' : '-')}</p></div>
                <div><p className="text-gray-500">Driver</p><p className={`font-semibold ${isPending && !dispatch.driver_name ? 'text-red-400 italic' : ''}`}>{dispatch.driver_name || (isPending ? 'Not filled' : '-')} {dispatch.driver_phone && `(${dispatch.driver_phone})`}</p></div>
                <div><p className="text-gray-500">Driver Contact</p><p className={`font-semibold ${isPending && !dispatch.driver_phone ? 'text-red-400 italic' : ''}`}>{dispatch.driver_phone || (isPending ? 'Not filled' : '-')}</p></div>
                <div><p className="text-gray-500">LR Number</p><p className="font-semibold">{dispatch.lr_number || '-'}</p></div>
                <div><p className="text-gray-500">E-Way Bill</p><p className="font-semibold">{dispatch.eway_bill || '-'}</p></div>
                <div><p className="text-gray-500">Expected Delivery</p><p className="font-semibold">{dispatch.expected_delivery || '-'}</p></div>
              </div>
              <div><p className="text-gray-500">Destination</p><p className="font-semibold">{dispatch.to_location || dispatch.delivery_address || '-'}</p></div>

              {/* Freight Details */}
              <div className="mt-3 pt-3 border-t border-gray-100">
                <h4 className="text-[10px] font-bold text-gray-500 uppercase mb-2">Freight Details</h4>
                <div className="grid grid-cols-3 gap-2">
                  <div><p className="text-gray-500">Total Freight</p><p className={`font-semibold ${isPending && !(dispatch.total_freight > 0) ? 'text-red-400 italic' : ''}`}>{dispatch.total_freight > 0 ? dispatch.total_freight.toLocaleString('en-IN') : (isPending ? 'Not filled' : '-')}</p></div>
                  <div><p className="text-gray-500">Advance</p><p className={`font-semibold ${isPending && (dispatch.advance_freight === null || dispatch.advance_freight === undefined || dispatch.advance_freight === 0 && dispatch.total_freight === 0) ? 'text-red-400 italic' : ''}`}>{dispatch.total_freight > 0 || dispatch.advance_freight > 0 ? (dispatch.advance_freight || 0).toLocaleString('en-IN') : (isPending ? 'Not filled' : '-')}</p></div>
                  <div><p className="text-gray-500">Rest Amount</p><p className="font-semibold text-amber-700">{dispatch.total_freight > 0 || dispatch.rest_freight > 0 ? (dispatch.rest_freight || 0).toLocaleString('en-IN') : '-'}</p></div>
                </div>
              </div>

              {dispatch.notes && <div><p className="text-gray-500">Notes</p><p className="text-gray-700">{dispatch.notes}</p></div>}
            </div>
          )}

          {/* Extra Charges Section */}
          <ExtraChargesSection dispatch={dispatch} onUpdate={d => setDispatch(d)} />
        </div>

        {/* Items */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100"><h3 className="text-xs font-bold text-gray-900">Dispatch Items</h3></div>
          <div className="divide-y divide-gray-50">
            {(dispatch.items || []).map((item, i) => (
              <div key={i} className="flex items-center justify-between px-4 py-3 text-xs">
                <div className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded bg-gray-100 text-[10px] font-bold text-gray-500 flex items-center justify-center">{i + 1}</span>
                  <span className="font-semibold text-gray-900">{item.name || item.product}</span>
                </div>
                <span className="font-mono font-semibold">{item.qty} {item.unit}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Attachments — visible for In Transit and Delivered */}
      {(dispatch.status === 'In Transit' || dispatch.status === 'Delivered') && (
        <div className="mt-5 bg-white rounded-xl border border-gray-100 p-4" data-testid="dispatch-attachments">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-bold text-gray-900">Attachments</h3>
            <label className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white rounded-lg cursor-pointer hover:opacity-90" style={{ backgroundColor: '#24549c' }}>
              {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
              {uploading ? 'Uploading...' : 'Upload File'}
              <input type="file" className="hidden" onChange={handleAttachmentUpload} disabled={uploading} data-testid="attachment-upload-input" />
            </label>
          </div>
          {(dispatch.attachments || []).length > 0 ? (
            <div className="space-y-2">
              {dispatch.attachments.map((url, i) => {
                const fileName = url.split('/').pop() || `Attachment ${i + 1}`;
                return (
                  <a key={i} href={url} target="_blank" rel="noopener noreferrer" data-testid={`attachment-link-${i}`}
                    className="flex items-center gap-2 px-3 py-2 text-xs bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <FileText className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
                    <span className="text-blue-700 font-medium truncate">{fileName}</span>
                  </a>
                );
              })}
            </div>
          ) : (
            <p className="text-[10px] text-gray-400 italic">No attachments yet. Upload receiving copy, delivery proof, or any related documents.</p>
          )}
        </div>
      )}

      {/* Edit Vehicle Number Dialog (Awaiting Invoice only) */}
      {showEditVehicleDialog && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4" data-testid="dsp-edit-vehicle-dialog">
          <div className="bg-white rounded-xl shadow-xl p-5 w-full max-w-sm">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                <Truck className="w-4 h-4 text-amber-600" />
              </div>
              <h3 className="text-sm font-bold text-gray-900">Edit Vehicle Number</h3>
            </div>
            <p className="text-[11px] text-gray-600 mb-3">
              Change the vehicle number for <span className="font-semibold">{dispatch?.dispatch_number}</span>{dispatch?.stn_number ? <> (linked to <span className="font-semibold">{dispatch.stn_number}</span>)</> : null}.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 mb-3">
              <p className="text-[10px] text-blue-900">
                💡 Editable only while the dispatch is awaiting invoice. Once the invoice is uploaded, the vehicle number is locked so the printed Tally invoice and ERP record always match.
              </p>
            </div>
            <label className="text-[11px] font-semibold text-gray-600 mb-1 block">New Vehicle Number <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={editVehicleValue}
              onChange={e => setEditVehicleValue(e.target.value.toUpperCase())}
              placeholder="e.g. MH01AB1234"
              data-testid="dsp-edit-vehicle-input"
              maxLength={20}
              className="w-full px-3 py-2 text-xs border border-amber-200 rounded-lg uppercase font-mono focus:outline-none focus:ring-2 focus:ring-amber-300"
            />
            <p className="text-[10px] text-gray-500 mt-1">Current: <span className="font-mono">{dispatch?.vehicle_no || '-'}</span></p>
            <div className="flex gap-2 justify-end mt-4">
              <button
                onClick={() => setShowEditVehicleDialog(false)}
                disabled={savingVehicle}
                className="px-4 py-2 text-xs font-semibold text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                data-testid="dsp-edit-vehicle-save-btn"
                onClick={handleSaveDispatchVehicleNumber}
                disabled={savingVehicle || !editVehicleValue.trim()}
                className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white rounded-lg bg-amber-600 hover:bg-amber-700 disabled:opacity-50"
              >
                {savingVehicle ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                Update Vehicle
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Dispatch Dialog (Admin/Manager) */}
      {showCancelDialog && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4" data-testid="dsp-cancel-dialog">
          <div className="bg-white rounded-xl shadow-xl p-5 w-full max-w-sm">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                <Ban className="w-4 h-4 text-red-600" />
              </div>
              <h3 className="text-sm font-bold text-gray-900">Cancel Dispatch</h3>
            </div>
            <p className="text-[11px] text-gray-600 mb-3">
              Cancel <span className="font-semibold">{dispatch?.dispatch_number}</span> (currently <span className="font-semibold">{dispatch?.status}</span>).
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 mb-3">
              <p className="text-[10px] text-amber-900">
                ⚠ The dispatched quantity will be added back to stock. The parent OC status is left unchanged. This cannot be undone.
              </p>
            </div>
            <label className="text-[11px] font-semibold text-gray-600 mb-1 block">Cancellation Reason <span className="text-red-500">*</span></label>
            <textarea
              value={cancelReason}
              onChange={e => setCancelReason(e.target.value)}
              placeholder="e.g. Legacy OC cancelled earlier; restoring stock"
              rows={3}
              data-testid="dsp-cancel-reason-input"
              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-300"
            />
            <div className="flex gap-2 justify-end mt-4">
              <button
                onClick={() => { setShowCancelDialog(false); setCancelReason(''); }}
                disabled={cancelling}
                className="px-4 py-2 text-xs font-semibold text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Keep Dispatch
              </button>
              <button
                data-testid="dsp-cancel-confirm-btn"
                onClick={handleCancelDispatch}
                disabled={cancelling || !cancelReason.trim()}
                className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white rounded-lg bg-red-600 hover:bg-red-700 disabled:opacity-50"
              >
                {cancelling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Ban className="w-3.5 h-3.5" />}
                Cancel Dispatch
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DispatchModule;
